/**
 * "APLIQUE AGORA" — a troca com VANTAGEM EXPLÍCITA e nenhum prejuízo.
 *
 * A regra, na palavra do dono: entra toda campanha melhor que a atual que não
 * piore o outro lado. São os três casos:
 *   - líquido MAIOR e o cliente pagando MENOS;
 *   - líquido MAIOR e o cliente pagando o MESMO;
 *   - líquido IGUAL e o cliente pagando MENOS.
 * Fica de fora só o que tem contrapartida (ganhar mais cobrando mais caro,
 * baixar o preço recebendo menos) e o empate nos dois lados, que não é
 * vantagem nenhuma. Exigir melhora ESTRITA dos dois lados ao mesmo tempo —
 * como esta tela fazia até 22/09 — descarta justamente o caso mais comum: a
 * campanha que paga mais rebate sem mexer no preço da vitrine.
 *
 * A RÉGUA NÃO MORA MAIS AQUI (29/09). Ela passou para o backend
 * (`backend_v2/src/modules/campaigns/applyNowRule.js`) porque o card "Anúncios
 * com campanha melhor para aplicar" da Visão geral conta a MESMA fila, e duas
 * cópias da régua — uma no servidor contando, outra aqui listando — é como o
 * card e a tela passariam a discordar no primeiro ajuste. O `/campaigns/items`
 * devolve cada campanha já marcada:
 *   - `is_applicable_now` — campanha em andamento no ML (status `started` E
 *     dentro do período `campaign_start_date`/`campaign_finish_date`, que vêm
 *     na mesma linha; o prazo de adesão fica fora, de propósito), anúncio
 *     ainda candidato (`candidate`/`pending`), preços conhecidos e vantagem
 *     sem contrapartida;
 *   - `switch_advantage` — 'both' | 'seller' | 'consumer' (null fora da fila);
 *   - `switch_rank` — 1..n entre as aplicáveis do anúncio (maior ganho no
 *     líquido, depois maior economia do cliente, depois campaign_id).
 * Este arquivo CONSOME as marcas: filtra, escolhe a primeira do rank e ordena
 * a fila. O "Radar de Rebate" é outra coisa: ordena por diferença de REBATE e
 * mostra de propósito as trocas que derrubam o líquido, para investigar; aqui
 * a pergunta é "o que eu aplico HOJE sem pensar duas vezes?".
 *
 * NADA de dinheiro é recalculado neste arquivo (CLAUDE.md §4). `seller_gain` e
 * `consumer_savings` vêm prontos do backend (`ComputeCampaignSavings`), sempre
 * medidos contra a MESMA base da tela: a campanha ativa do anúncio ou, quando
 * não há nenhuma, o preço do próprio anúncio. Aqui só se FILTRA e ORDENA.
 */
import { IsNum } from './campaignCardShared';

/**
 * Ordem do backend (`switch_rank`, 1 = melhor troca). Rank ausente desce para
 * o fim em vez de embaralhar a fila; empates mantêm a ordem recebida (o sort é
 * estável), que já é a do backend.
 */
function bySwitchRank(a, b) {
    const rankA = IsNum(a.switch_rank) ? Number(a.switch_rank) : Number.POSITIVE_INFINITY;
    const rankB = IsNum(b.switch_rank) ? Number(b.switch_rank) : Number.POSITIVE_INFINITY;
    if (rankA === rankB) {
        return 0;
    }
    return rankA < rankB ? -1 : 1;
}

/**
 * Guarda de deploy: há anúncios com campanhas mas NENHUMA campanha traz a
 * chave `is_applicable_now` — o frontend novo está falando com um backend que
 * ainda não marca a fila. Sem esta guarda a tela diria "nenhum anúncio tem
 * troca aplicável", que é falso: o que falta é a marca, não a oportunidade.
 * Sem campanha nenhuma não há o que julgar, e a fila vazia é verdadeira.
 */
export function IsApplyNowMarkMissing(items = []) {
    const campaigns = (Array.isArray(items) ? items : []).flatMap((item) =>
        Array.isArray(item?.campaigns) ? item.campaigns : [],
    );
    if (campaigns.length === 0) {
        return false;
    }
    return !campaigns.some((campaign) => campaign && typeof campaign === 'object' && 'is_applicable_now' in campaign);
}

/**
 * Linha do anúncio na fila, ou `null` quando nenhuma candidatura dele passa.
 *
 * A campanha ATUAL vai como `current` só para rotular a coluna "hoje"; os
 * números dela são os do próprio item (`price_consumer`/`price_net`), que é a
 * base contra a qual o backend calculou os deltas — inclusive quando não há
 * campanha ativa nenhuma e a base é o preço do anúncio.
 *
 * `allowSuggested=false` derruba as candidaturas cujo preço é SUGESTÃO do ML
 * (`price_is_suggested`) ANTES da escolha, e não depois: descartar a linha
 * pronta esconderia o anúncio inteiro quando a estimativa é só a primeira da
 * fila e existe atrás dela uma oferta firme que também passa na régua.
 */
export function BuildApplyNowRow(item, { allowSuggested = true } = {}) {
    const campaigns = Array.isArray(item?.campaigns) ? item.campaigns : [];
    const aplicaveis = campaigns
        .filter(
            (campaign) =>
                campaign?.is_applicable_now === true && (allowSuggested || campaign.price_is_suggested !== true),
        )
        .sort(bySwitchRank);
    if (aplicaveis.length === 0) {
        return null;
    }

    const [target, ...alternativas] = aplicaveis;
    const netGain = Number(target.seller_gain);
    const sales7d = IsNum(item?.sales_7d_total) ? Number(item.sales_7d_total) : null;

    return {
        item,
        current: campaigns.find((campaign) => campaign?.is_current === true) || null,
        target,
        // 'both' | 'seller' | 'consumer' — de que lado está a vantagem, como o
        // backend classificou (`switch_advantage`).
        advantage: target.switch_advantage,
        net_gain: netGain,
        consumer_savings: Number(target.consumer_savings),
        sales_7d_total: sales7d,
        // ESTIMATIVA declarada: ganho por venda × o que o anúncio vendeu nos
        // últimos 7 dias. Não é promessa de receita — é a régua que separa
        // "R$ 0,30 num anúncio que vende 7.500" de "R$ 3,00 num que vende 2".
        // Sem vendas conhecidas fica `null`, nunca 0 (0 significaria "não
        // vendeu", e aqui o dado é que NÃO SE SABE).
        impact_7d: sales7d === null ? null : netGain * sales7d,
        alternatives: alternativas,
    };
}

/** Fila inteira: um anúncio por linha, só os que têm troca aplicável. */
export function BuildApplyNowRows(items = [], options = {}) {
    return (Array.isArray(items) ? items : []).map((item) => BuildApplyNowRow(item, options)).filter(Boolean);
}

/**
 * Ordenações da fila. `impact_7d` é o padrão porque é a única que responde
 * "por onde começo?" — as outras três respondem "qual é a maior?".
 */
export const APPLY_NOW_SORTS = [
    ['impact_7d', 'Impacto 7d'],
    ['net_gain', 'Ganho por venda'],
    ['consumer_savings', 'Economia do cliente'],
    ['sales_7d_total', 'Vendas 7d'],
];

/** Linha sem o valor do critério desce para o fim (nunca some da fila). */
function compareByField(field) {
    return (a, b) => {
        const valueA = IsNum(a[field]) ? Number(a[field]) : null;
        const valueB = IsNum(b[field]) ? Number(b[field]) : null;
        if (valueA === null && valueB === null) {
            return 0;
        }
        if (valueA === null) {
            return 1;
        }
        if (valueB === null) {
            return -1;
        }
        if (valueA !== valueB) {
            return valueB - valueA;
        }
        return String(a.item?.item_id || '').localeCompare(String(b.item?.item_id || ''));
    };
}

export function SortApplyNowRows(rows = [], sortBy = 'impact_7d') {
    const campo = APPLY_NOW_SORTS.some(([id]) => id === sortBy) ? sortBy : 'impact_7d';
    return [...rows].sort(compareByField(campo));
}

/**
 * Recorte da fila pela busca: MLB, SKU ou título (o mesmo trio do monitor).
 * O corte por preço estimado NÃO mora aqui — ele muda qual campanha é a
 * escolhida, então acontece em `BuildApplyNowRow`.
 */
export function FilterApplyNowRows(rows = [], { search = '' } = {}) {
    const termo = String(search || '')
        .trim()
        .toLowerCase();
    if (!termo) {
        return rows;
    }

    return rows.filter((row) => {
        const item = row.item || {};
        return [item.item_id, item.sku, item.title].some((campo) =>
            String(campo || '')
                .toLowerCase()
                .includes(termo),
        );
    });
}

/** Totais do cabeçalho. Soma só o que existe; ausente não vira 0. */
export function SummarizeApplyNowRows(rows = []) {
    return rows.reduce(
        (acumulado, row) => ({
            items: acumulado.items + 1,
            net_gain: acumulado.net_gain + (IsNum(row.net_gain) ? Number(row.net_gain) : 0),
            impact_7d: acumulado.impact_7d + (IsNum(row.impact_7d) ? Number(row.impact_7d) : 0),
            // Quantas linhas teriam impacto se houvesse venda conhecida — o
            // rodapé precisa dizer quando o total está incompleto.
            without_sales: acumulado.without_sales + (IsNum(row.impact_7d) ? 0 : 1),
            // A mistura da fila: com a régua larga, "sobe o líquido sem o
            // cliente sentir" e "baixa o preço sem eu perder" convivem, e o
            // cabeçalho precisa dizer quanto tem de cada.
            by_advantage: {
                ...acumulado.by_advantage,
                [row.advantage]: (acumulado.by_advantage[row.advantage] || 0) + 1,
            },
        }),
        {
            items: 0,
            net_gain: 0,
            impact_7d: 0,
            without_sales: 0,
            by_advantage: { both: 0, seller: 0, consumer: 0 },
        },
    );
}
